import { useQuery } from '@tanstack/react-query'

import { useWorkspaceId } from '@/features/chatbots/queries'
import { inboxApi } from '@/features/inbox/api'
import type { InboxCounts } from '@/features/inbox/types'
import { tasksApi } from '@/features/tasks/api'
import { keys } from '@/lib/api/keys'
import { P } from '@/lib/permissions/catalog'
import { useCan } from '@/lib/permissions/useCan'

export interface NavBadges {
  /** Keyed by NavItem.badge. Undefined means "no number to show". */
  unread?: number
  tasksDue?: number
  hotLeads?: number
  /** Per-view counts for the Conversations sub-items. */
  inbox?: InboxCounts
}

/**
 * The numbers on the sidebar.
 *
 * Only counts a purpose-built endpoint already returns are shown. Two rules
 * kept this from turning into a pile of requests fired on every screen:
 *
 * NOTHING IS INVENTED. A badge with no cheap source (live visitors, open
 * knowledge gaps) is not rendered at all rather than filled by downloading a
 * whole list to call `.length` on it. Those need a `/count` endpoint first.
 *
 * NOTHING IS FETCHED TWICE. Both queries reuse the exact keys the Inbox and My
 * Day screens use, so opening either screen costs no extra request and the
 * badge updates from the same cache the page writes to.
 */
export function useNavBadges(): NavBadges {
  const ws = useWorkspaceId()
  const canConversations = useCan(P.conversationsView)

  const counts = useQuery({
    // The exact key and payload ConversationList uses with an empty search
    // box, so the inbox and the sidebar share one request.
    queryKey: keys.inbox.counts(ws ?? '', { search: '' }),
    queryFn: () => inboxApi.counts(ws!),
    enabled: Boolean(ws) && canConversations,
    staleTime: 30_000,
    retry: false,
  })

  const day = useQuery({
    // Matches MyDayPage's key, so opening My Day costs nothing extra.
    queryKey: ['my-day', ws],
    queryFn: () => tasksApi.myDay(ws!),
    enabled: Boolean(ws),
    staleTime: 60_000,
    retry: false,
  })

  const tasksDue =
    day.data && day.data.tasks.length + day.data.followups.length
  const hotLeads = day.data?.hot_leads.length

  return {
    ...(counts.data ? { unread: counts.data.unread, inbox: counts.data } : {}),
    ...(tasksDue ? { tasksDue } : {}),
    ...(hotLeads ? { hotLeads } : {}),
  }
}
